import { LitElement, html } from 'lit'
import '../components/floating-button.js'
import '../components/modal-transacao.js'
import '../components/premium-guard.js'
import '../components/skeleton-placeholder.js'
import { contaCacheService } from '../core/services/conta-cache.service.js'
import { saldoMensalCacheService } from '../core/services/saldo-mensal-cache.service.js'
import { transacaoCacheService } from '../core/services/transacao-cache.service.js'
import { cacheService } from '../core/services/cache.service.js'
import { ProtectedPageMixin } from '../core/mixins/protected-page.mixin.js'

/**
 * Timeout em milissegundos para auto-remoção de skeleton (15 segundos)
 * Se o evento cache-operation-finished ou xxx-cache-updated não chegar
 * dentro deste período, o skeleton é removido automaticamente.
 * @type {number}
 */
const SKELETON_TIMEOUT_MS = 15000

export class DashboardPage extends ProtectedPageMixin(LitElement) {
  static properties = {
    contas: { type: Array },
    /** @type {number} Patrimônio total obtido do backend via saldo mensal cache (exclui cartão de crédito) */
    patrimonioTotal: { type: Number },
    receitasMes: { type: Number },
    despesasMes: { type: Number },
    saldoLiquido: { type: Number },
    gastoPessoal: { type: Number },
    gastoCompartilhado: { type: Number },
    ultimasTransacoes: { type: Array },
    loading: { type: Boolean },
    loadingSaldo: { type: Boolean },
    offlineSemCache: { type: Boolean },
    authError: { type: String },
    /** @type {boolean} Skeleton ativo no card de patrimônio total */
    _skeletonPatrimonio: { type: Boolean, state: true },
    /** @type {boolean} Skeleton ativo no card de receitas do mês */
    _skeletonReceitas: { type: Boolean, state: true },
    /** @type {boolean} Skeleton ativo no card de despesas do mês */
    _skeletonDespesas: { type: Boolean, state: true },
    /** @type {boolean} Skeleton ativo no card de saldo líquido */
    _skeletonSaldo: { type: Boolean, state: true }
  }

  createRenderRoot() {
    return this
  }

  constructor() {
    
    super()
    this.contas = []
    /**
     * Patrimônio total vindo do backend via saldo mensal cache.
     * Substituiu o cálculo local redundante (calcularSaldoTotal) que compensava
     * o bug do backend. Agora o backend calcula corretamente excluindo cartão de crédito.
     * @type {number}
     */
    this.patrimonioTotal = 0
    this.receitasMes = 0
    this.despesasMes = 0
    this.saldoLiquido = 0
    /** @type {number} Valor total de gastos pessoais do mês atual */
    this.gastoPessoal = 0
    /** @type {number} Valor total de gastos compartilhados do mês atual */
    this.gastoCompartilhado = 0
    /** @type {Array} Últimas 5 transações do mês atual ordenadas por data decrescente */
    this.ultimasTransacoes = []
    this.loading = true
    this.loadingSaldo = true
    this.offlineSemCache = false
    /** @type {string|null} Mensagem de erro de autenticação (timeout ou falha) */
    this.authError = null

    // Estado de skeleton para cards com dados calculados
    this._skeletonPatrimonio = false
    this._skeletonReceitas = false
    this._skeletonDespesas = false
    this._skeletonSaldo = false
    /** @type {number|null} ID do timeout para auto-remoção de skeleton */
    this._skeletonTimeoutId = null

    // Inicializa serviços com cache inteligente
    this.contaCacheService = contaCacheService
    this.saldoMensalService = saldoMensalCacheService
    this.transacaoCacheService = transacaoCacheService

    // Bind dos handlers para remoção correta no disconnectedCallback
    this._boundHandleContasCacheUpdated = this._handleContasCacheUpdated.bind(this)
    this._boundHandleTransacoesCacheUpdated = this._handleTransacoesCacheUpdated.bind(this)
    this._boundHandleSaldoMensalCacheUpdated = this._handleSaldoMensalCacheUpdated.bind(this)
    this._boundHandleCategoriasCacheUpdated = this._handleCategoriasCacheUpdated.bind(this)
    this._boundHandleCacheOperationStarted = this._handleCacheOperationStarted.bind(this)
    this._boundHandleCacheOperationFinished = this._handleCacheOperationFinished.bind(this)
  }

  async connectedCallback() {
    super.connectedCallback()
    this._loadWithAuthGuard(async () => {
      await this.carregarDados()
    })

    // Listeners de eventos de cache para atualização reativa da UI
    window.addEventListener('contas-cache-updated', this._boundHandleContasCacheUpdated)
    window.addEventListener('transacoes-cache-updated', this._boundHandleTransacoesCacheUpdated)
    window.addEventListener('saldo-mensal-cache-updated', this._boundHandleSaldoMensalCacheUpdated)
    window.addEventListener('categorias-cache-updated', this._boundHandleCategoriasCacheUpdated)

    // Listeners de ciclo de vida de operações de cache (skeleton)
    window.addEventListener('cache-operation-started', this._boundHandleCacheOperationStarted)
    window.addEventListener('cache-operation-finished', this._boundHandleCacheOperationFinished)
  }

  /**
   * Remove listeners de eventos e limpa timeouts ao desconectar o componente do DOM
   * Previne memory leaks e chamadas a componentes destruídos
   */
  disconnectedCallback() {
    super.disconnectedCallback()

    // Remove listeners de eventos de cache
    window.removeEventListener('contas-cache-updated', this._boundHandleContasCacheUpdated)
    window.removeEventListener('transacoes-cache-updated', this._boundHandleTransacoesCacheUpdated)
    window.removeEventListener('saldo-mensal-cache-updated', this._boundHandleSaldoMensalCacheUpdated)
    window.removeEventListener('categorias-cache-updated', this._boundHandleCategoriasCacheUpdated)

    // Remove listeners de ciclo de vida de operações
    window.removeEventListener('cache-operation-started', this._boundHandleCacheOperationStarted)
    window.removeEventListener('cache-operation-finished', this._boundHandleCacheOperationFinished)

    // Limpa timeout de skeleton pendente
    if (this._skeletonTimeoutId) {
      clearTimeout(this._skeletonTimeoutId)
      this._skeletonTimeoutId = null
    }
  }

  /**
   * Manipula evento cache-operation-started para ativar skeleton nos cards afetados
   * Mapeia a entidade da operação para os cards correspondentes no dashboard.
   * Inicia timeout de 15s para auto-remoção do skeleton caso o evento de conclusão não chegue.
   *
   * @param {CustomEvent} event - Evento com detail { entidade, operacao }
   * @private
   */
  _handleCacheOperationStarted(event) {
    const { entidade } = event.detail

    // Mapeia entidade → cards afetados no dashboard
    if (['contas', 'transacoes'].includes(entidade)) {
      this._skeletonPatrimonio = true
      this._skeletonSaldo = true
    }
    if (['transacoes'].includes(entidade)) {
      this._skeletonReceitas = true
      this._skeletonDespesas = true
    }
    if (['categorias'].includes(entidade)) {
      this._skeletonDespesas = true
    }

    // Inicia timeout de 15s para auto-remoção do skeleton
    if (this._skeletonTimeoutId) {
      clearTimeout(this._skeletonTimeoutId)
    }
    this._skeletonTimeoutId = setTimeout(() => {
      this._skeletonPatrimonio = false
      this._skeletonReceitas = false
      this._skeletonDespesas = false
      this._skeletonSaldo = false
      this._skeletonTimeoutId = null
      this.requestUpdate()
    }, SKELETON_TIMEOUT_MS)
  }

  /**
   * Manipula evento cache-operation-finished para desativar skeleton em todos os cards
   * Limpa o timeout de auto-remoção e restaura a exibição dos valores reais.
   *
   * @param {CustomEvent} _event - Evento com detail { entidade, sucesso }
   * @private
   */
  _handleCacheOperationFinished(_event) {
    if (this._skeletonTimeoutId) {
      clearTimeout(this._skeletonTimeoutId)
      this._skeletonTimeoutId = null
    }
    this._skeletonPatrimonio = false
    this._skeletonReceitas = false
    this._skeletonDespesas = false
    this._skeletonSaldo = false
  }

  /**
   * Manipula evento contas-cache-updated lendo dados do cache de forma síncrona
   * Não chama await service.listar() — lê diretamente do cache local.
   * Implementa guard contra null/undefined para manter valor atual.
   *
   * @private
   */
  _handleContasCacheUpdated() {
    const contas = cacheService.get('contas')
    if (contas != null) {
      this.contas = contas.filter(conta => conta.ativo !== false)
      this.requestUpdate()
    }
  }

  /**
   * Manipula evento transacoes-cache-updated lendo dados do cache de forma síncrona
   * Busca transações do mês atual no cache e atualiza últimas transações.
   * Implementa guard contra null/undefined para manter valor atual.
   *
   * @private
   */
  _handleTransacoesCacheUpdated() {
    const hoje = new Date()
    const mes = String(hoje.getMonth() + 1).padStart(2, '0')
    const ano = String(hoje.getFullYear())
    const cacheKey = `transacoes_${mes}_${ano}`

    const transacoes = cacheService.get(cacheKey)
    if (transacoes != null) {
      // Ordena por data decrescente (formato DD/MM/AAAA) e pega as 5 mais recentes
      this.ultimasTransacoes = [...transacoes]
        .sort((a, b) => {
          const [diaA, mesA, anoA] = a.data.split('/')
          const [diaB, mesB, anoB] = b.data.split('/')
          const dateA = new Date(anoA, mesA - 1, diaA)
          const dateB = new Date(anoB, mesB - 1, diaB)
          return dateB - dateA
        })
        .slice(0, 5)
      this.requestUpdate()
    }
  }

  /**
   * Manipula evento saldo-mensal-cache-updated lendo dados do cache de forma síncrona
   * Atualiza patrimônio total, receitas, despesas e saldo líquido a partir do cache.
   * Desativa skeleton dos cards afetados após receber dados atualizados.
   * Implementa guard contra null/undefined para manter valor atual.
   *
   * @private
   */
  _handleSaldoMensalCacheUpdated() {
    const hoje = new Date()
    const mes = String(hoje.getMonth() + 1).padStart(2, '0')
    const ano = String(hoje.getFullYear())
    const cacheKey = `saldo_mensal_${mes}_${ano}`

    const saldo = cacheService.get(cacheKey)
    if (saldo != null) {
      this.receitasMes = Math.abs(saldo.totalReceitas)
      this.despesasMes = Math.abs(saldo.totalDespesas)
      this.saldoLiquido = this.receitasMes - this.despesasMes
      this.gastoPessoal = saldo.gastoPessoal || 0
      this.gastoCompartilhado = saldo.gastoCompartilhado || 0
      this.patrimonioTotal = saldo.patrimonioTotal || 0

      // Desativa skeleton dos cards de saldo após receber dados
      this._skeletonPatrimonio = false
      this._skeletonReceitas = false
      this._skeletonDespesas = false
      this._skeletonSaldo = false

      if (this._skeletonTimeoutId) {
        clearTimeout(this._skeletonTimeoutId)
        this._skeletonTimeoutId = null
      }

      this.requestUpdate()
    }
  }

  /**
   * Manipula evento categorias-cache-updated lendo dados do cache de forma síncrona
   * Desativa skeleton do card de despesas após atualização de categorias.
   * Categorias afetam o card de despesas por categoria no dashboard.
   *
   * @private
   */
  _handleCategoriasCacheUpdated() {
    // Categorias atualizadas — desativa skeleton de despesas
    this._skeletonDespesas = false
    this.requestUpdate()
  }

  /**
   * Carrega todos os dados do dashboard em paralelo
   * Inclui contas (saldos individuais), resumo mensal (receitas/despesas)
   * e últimas transações do mês atual.
   * A espera pelo auth é gerenciada pelo ProtectedPageMixin via _loadWithAuthGuard.
   */
  async carregarDados() {
    this.loading = true
    this.loadingSaldo = true
    try {
      await Promise.all([
        this.carregarContas(),
        this._carregarResumoMensal(),
        this._carregarUltimasTransacoes()
      ])
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error)
    } finally {
      this.loading = false
    }
  }

  /**
   * Carrega lista de contas ativas via contaCacheService
   * Mantém saldos individuais por conta para exibição no card "Saldos por Conta"
   * O patrimônio total não é mais calculado localmente — vem do backend via saldo mensal cache
   */
  async carregarContas() {
    try {
      const todasContas = await this.contaCacheService.listar()
      this.contas = todasContas.filter(conta => conta.ativo !== false) // Filtrar ativas
    } catch (error) {
      console.error('Erro ao carregar contas:', error)
    }
  }

  /**
   * Carrega resumo financeiro do mês atual via saldoMensalCacheService
   * Usa dados pré-calculados do backend (1 leitura Firestore) ao invés de
   * buscar N transações e calcular localmente.
   * Extrai também gastoPessoal e gastoCompartilhado para o card de responsáveis.
   * Trata cenário offline sem cache exibindo mensagem informativa.
   *
   * @private
   */
  async _carregarResumoMensal() {
    this.loadingSaldo = true
    this.offlineSemCache = false

    try {
      const saldo = await this.saldoMensalService.obterMesAtual()

      if (saldo) {
        // Aplica Math.abs() para proteger contra dados legados onde
        // totalDespesas pode estar armazenado como valor negativo
        this.receitasMes = Math.abs(saldo.totalReceitas)
        this.despesasMes = Math.abs(saldo.totalDespesas)
        // Recalcula saldo líquido no frontend para garantir subtração correta
        this.saldoLiquido = this.receitasMes - this.despesasMes
        // Extrai valores de gastos por responsável do saldo mensal pré-calculado
        this.gastoPessoal = saldo.gastoPessoal || 0
        this.gastoCompartilhado = saldo.gastoCompartilhado || 0
        // Patrimônio total calculado pelo backend (já exclui cartão de crédito)
        this.patrimonioTotal = saldo.patrimonioTotal || 0
      } else {
        // Offline sem cache: exibe mensagem informativa
        this.offlineSemCache = true
        this.receitasMes = 0
        this.despesasMes = 0
        this.saldoLiquido = 0
        this.gastoPessoal = 0
        this.gastoCompartilhado = 0
        this.patrimonioTotal = 0
      }
    } catch (error) {
      console.error('Erro ao carregar resumo mensal:', error)
      this.offlineSemCache = !navigator.onLine
    } finally {
      this.loadingSaldo = false
    }
  }

  /**
   * Carrega as últimas 5 transações do mês atual via transacaoCacheService
   * Ordena por data decrescente e limita a 5 registros para exibição no dashboard.
   * Em caso de erro, mantém lista vazia para exibir estado vazio graciosamente.
   *
   * @private
   */
  async _carregarUltimasTransacoes() {
    try {
      const hoje = new Date()
      const mes = String(hoje.getMonth() + 1).padStart(2, '0')
      const ano = String(hoje.getFullYear())

      const transacoes = await this.transacaoCacheService.listarPorPeriodo(mes, ano)

      // Ordena por data decrescente (formato DD/MM/AAAA) e pega as 5 mais recentes
      this.ultimasTransacoes = [...transacoes]
        .sort((a, b) => {
          const [diaA, mesA, anoA] = a.data.split('/')
          const [diaB, mesB, anoB] = b.data.split('/')
          const dateA = new Date(anoA, mesA - 1, diaA)
          const dateB = new Date(anoB, mesB - 1, diaB)
          return dateB - dateA
        })
        .slice(0, 5)
    } catch (error) {
      console.error('Erro ao carregar últimas transações:', error)
      this.ultimasTransacoes = []
    }
  }

  /**
   * Formata valor numérico para moeda brasileira (BRL)
   * @param {number} valor - Valor a ser formatado
   * @returns {string} Valor formatado (ex: 'R$ 1.500,00')
   */
  formatarMoeda(valor) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor || 0)
  }

  _handleAddTransaction() {
    const modal = this.renderRoot.querySelector('modal-transacao')
    if (modal) {
      modal.show()
    }
  }

  /**
   * Renderiza mensagem de estado offline sem cache disponível
   * Informa ao usuário que não há dados para exibir sem conexão
   * @returns {import('lit').TemplateResult} Template HTML da mensagem offline
   * @private
   */
  _renderOfflineMessage() {
    return html`
      <div class="notification is-warning is-light" data-cy="resumo-offline-message">
        <span class="icon">
          <i class="fas fa-wifi-slash"></i>
        </span>
        <span>Sem conexão e sem dados em cache. Conecte-se à internet para carregar o resumo financeiro.</span>
      </div>
    `
  }

  render() {
    return html`
      <premium-guard>
        <section class="section">
          <div class="container" style="padding-bottom: 90px;">
            <div class="is-flex is-justify-content-space-between is-align-items-center mb-5">
              <h1 class="title is-4" data-cy="dashboard-title">Dashboard</h1>
            </div>

          ${this.offlineSemCache ? this._renderOfflineMessage() : ''}
          ${this.authError ? html`
            <div class="notification is-danger is-light" data-cy="auth-error-message">
              <span class="icon">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <span>${this.authError}</span>
            </div>
          ` : ''}

          <!-- === Linha 1 === -->
          <div class="columns is-variable is-4 dashboard-cards-row">
            <div class="column is-4 dashboard-card">
              <div class="card">
                <header class="card-header">
                  <p class="card-header-title" data-cy="card-saldo-total">Saldo Total</p>
                  <span class="icon has-text-grey">
                    <i class="fa-solid fa-wallet"></i>
                  </span>
                </header>
                <div class="card-content">
                  ${this._skeletonPatrimonio
                    ? html`<skeleton-placeholder name="saldo-total-valor" width="120px" height="2rem"></skeleton-placeholder>`
                    : html`<p class="is-size-5" data-cy="saldo-total-valor">
                        ${this.loading ? 'Carregando...' : this.formatarMoeda(this.patrimonioTotal)}
                      </p>`
                  }
                  <p class="is-size-7 has-text-grey">Excluindo faturas de cartão</p>
                </div>
              </div>
            </div>

            <div class="column is-4 dashboard-card">
              <div class="card">
                <header class="card-header">
                  <p class="card-header-title" data-cy="card-receitas-mes">Receitas do Mês</p>
                  <span class="icon has-text-success">
                    <i class="fa-solid fa-circle-arrow-up"></i>
                  </span>
                </header>
                <div class="card-content">
                  ${this._skeletonReceitas
                    ? html`<skeleton-placeholder name="receitas-valor" width="120px" height="2rem"></skeleton-placeholder>`
                    : html`<p class="is-size-5 has-text-success" data-cy="receitas-mes-valor">
                        ${this.loadingSaldo ? 'Carregando...' : this.formatarMoeda(this.receitasMes)}
                      </p>`
                  }
                </div>
              </div>
            </div>

            <div class="column is-4 dashboard-card">
              <div class="card">
                <header class="card-header">
                  <p class="card-header-title" data-cy="card-despesas-mes">Despesas do Mês</p>
                  <span class="icon has-text-danger">
                    <i class="fa-solid fa-circle-arrow-down"></i>
                  </span>
                </header>
                <div class="card-content">
                  ${this._skeletonDespesas
                    ? html`<skeleton-placeholder name="despesas-valor" width="120px" height="2rem"></skeleton-placeholder>`
                    : html`<p class="is-size-5 has-text-danger" data-cy="despesas-mes-valor">
                        ${this.loadingSaldo ? 'Carregando...' : this.formatarMoeda(this.despesasMes)}
                      </p>`
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- === Linha 2 === -->
          <div class="columns is-variable is-4">
            <!-- Resultado por Responsável (Mês) -->
            <div class="column is-6 dashboard-card-large">
              <div class="card">
                <header class="card-header">
                  <p class="card-header-title" data-cy="card-totais-responsavel">Resultado por Responsável (Mês)</p>
                </header>
                <div class="card-content">
                  <div class="content">
                    ${this.loadingSaldo ? html`
                      <div class="has-text-centered py-5" data-cy="responsavel-loading">
                        <p class="has-text-grey">Carregando...</p>
                      </div>
                    ` : this.gastoPessoal === 0 && this.gastoCompartilhado === 0 ? html`
                      <div class="has-text-centered py-5" data-cy="responsavel-vazio">
                        <span class="icon is-large has-text-grey-light">
                          <i class="fas fa-users fa-2x"></i>
                        </span>
                        <p class="has-text-grey mt-3 mb-2">Nenhum dado encontrado</p>
                        <p class="has-text-grey-light is-size-7">Adicione transações para visualizar os totais</p>
                      </div>
                    ` : html`
                      <div class="level is-mobile mb-3">
                        <div class="level-left">
                          <div class="level-item">
                            <span class="icon has-text-info mr-2"><i class="fas fa-user"></i></span>
                            <span>Gasto Pessoal</span>
                          </div>
                        </div>
                        <div class="level-right">
                          <div class="level-item">
                            <span class="has-text-weight-semibold" data-cy="gasto-pessoal-valor">${this.formatarMoeda(this.gastoPessoal)}</span>
                          </div>
                        </div>
                      </div>
                      <div class="level is-mobile mb-3">
                        <div class="level-left">
                          <div class="level-item">
                            <span class="icon has-text-warning mr-2"><i class="fas fa-users"></i></span>
                            <span>Gasto Compartilhado</span>
                          </div>
                        </div>
                        <div class="level-right">
                          <div class="level-item">
                            <span class="has-text-weight-semibold" data-cy="gasto-compartilhado-valor">${this.formatarMoeda(this.gastoCompartilhado)}</span>
                          </div>
                        </div>
                      </div>
                    `}
                  </div>
                </div>
              </div>
            </div>

            <!-- Saldos por Conta -->
            <div class="column is-6 dashboard-card-large">
              <div class="card">
                <header class="card-header">
                  <p class="card-header-title" data-cy="card-saldos-conta">Saldos por Conta</p>
                </header>
                <div class="card-content custom-scrollbar">
                  <div class="content">
                    ${this._skeletonSaldo ? html`
                      <skeleton-placeholder name="saldos-conta" width="100%" height="4rem"></skeleton-placeholder>
                    ` : this.loading ? html`
                      <div class="has-text-centered py-5">
                        <p class="has-text-grey">Carregando contas...</p>
                      </div>
                    ` : this.contas.length === 0 ? html`
                      <div class="has-text-centered py-5">
                        <span class="icon is-large has-text-grey-light">
                          <i class="fas fa-credit-card fa-2x"></i>
                        </span>
                        <p class="has-text-grey mt-3 mb-2">Nenhuma conta cadastrada</p>
                        <p class="has-text-grey-light is-size-7">Adicione contas para visualizar os saldos</p>
                      </div>
                    ` : html`
                      ${this.contas.map(conta => html`
                        <div class="level is-mobile mb-3">
                          <div class="level-left">
                            <div class="level-item">
                              <div>
                                <p class="has-text-weight-semibold">${conta.nome}</p>
                                <p class="is-size-7 has-text-grey">${conta.tipo}</p>
                              </div>
                            </div>
                          </div>
                          <div class="level-right">
                            <div class="level-item">
                              <p class="has-text-weight-semibold ${conta.tipo === 'Cartão de Crédito' ? 'has-text-danger' : 'has-text-success'}">
                                ${this.formatarMoeda(conta.saldoAtual || conta.saldoInicial || 0)}
                              </p>
                            </div>
                          </div>
                        </div>
                      `)}
                    `}
                  </div>
                </div>
                ${this.contas.length > 0 ? html`
                  <footer class="card-footer">
                    <a href="#/configuracoes" class="card-footer-item" data-cy="link-ver-todas-contas">Ver todas</a>
                  </footer>
                ` : ''}
              </div>
            </div>
          </div>

          <!-- === Linha 3 === -->
          <div class="columns is-variable is-4">
            <div class="column">
              <div class="card">
                <header class="card-header">
                  <p class="card-header-title" data-cy="card-ultimas-transacoes">Últimas Transações</p>
                </header>
                <div class="card-content custom-scrollbar">
                  <div class="content">
                    ${this.loading ? html`
                      <div class="has-text-centered py-5" data-cy="ultimas-transacoes-loading">
                        <p class="has-text-grey">Carregando...</p>
                      </div>
                    ` : this.ultimasTransacoes.length === 0 ? html`
                      <p class="has-text-grey has-text-centered" data-cy="ultimas-transacoes-vazio">Nenhuma transação encontrada no período atual</p>
                    ` : html`
                      ${this.ultimasTransacoes.map(t => html`
                        <div class="level is-mobile mb-3" data-cy="ultima-transacao-item">
                          <div class="level-left">
                            <div class="level-item">
                              <div>
                                <p class="has-text-weight-semibold is-size-7">${t.descricao}</p>
                                <p class="is-size-7 has-text-grey">${t.data}</p>
                              </div>
                            </div>
                          </div>
                          <div class="level-right">
                            <div class="level-item">
                              <span class="is-size-7 has-text-weight-semibold ${t.tipo === 'receita' ? 'has-text-success' : t.tipo === 'transferencia' ? 'has-text-info' : 'has-text-danger'}">
                                ${t.tipo === 'receita' ? '' : '-'}${this.formatarMoeda(Math.abs(t.valor))}
                              </span>
                            </div>
                          </div>
                        </div>
                      `)}
                    `}
                  </div>
                </div>
                <footer class="card-footer">
                  <a href="#/transacoes" class="card-footer-item" data-cy="link-ver-todas-transacoes">Ver todas</a>
                </footer>
              </div>
            </div>
          </div>
        </div>

          <floating-button action="add-transaction" @floating-button-click="${this._handleAddTransaction}"></floating-button>
          <modal-transacao></modal-transacao>
        </section>
      </premium-guard>
    `
  }
}

customElements.define('dashboard-page', DashboardPage)
